import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Spin, Button, Spacer } from '@brokerbay/design-components';
import {
  Button as ButtonV2,
  Color,
  Icon,
  Input,
} from '@bb/elements-web-rebranding';
import { isValidEmail } from 'utils';
import { Text } from 'components';
import {
  CanManageBillingProfileType,
  useSubscriptionsPageContext,
} from '../../SubscriptionsPageContext';
import { CardStatus } from './CardStatus';

type BillingEmailCardProps = {
  canManageBilling: CanManageBillingProfileType;
};

const BillingEmailCard: React.FC<BillingEmailCardProps> = ({
  canManageBilling,
}) => {
  const {
    billingProfile,
    updateBillingProfile,
    isFetchingProfile,
    isUpdatingProfile,
    hasSavedCard,
  } = useSubscriptionsPageContext();

  const [updatedEmail, setUpdatedEmail] = useState('');
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [updateProfileError, setUpdateProfileError] = useState<Error>();

  const switchToEditMode = useCallback(() => {
    if (canManageBilling.response) {
      setUpdatedEmail(billingProfile?.Email || '');
      setIsInEditMode(true);
    }
  }, [canManageBilling, billingProfile?.Email]);

  const closeEditMode = useCallback(() => {
    setUpdatedEmail('');
    setIsInEditMode(false);
  }, []);

  const updateEmail = useCallback(() => {
    if (canManageBilling.response) {
      const updatedProfile = { ...billingProfile, email: updatedEmail };
      setUpdateProfileError(undefined);
      updateBillingProfile(updatedProfile)
        .then(() => {
          closeEditMode();
        })
        .catch(error => {
          // Assume that any error is due to the email being invalid
          setUpdateProfileError(error);
        });
    }
  }, [
    canManageBilling,
    billingProfile,
    updateBillingProfile,
    updatedEmail,
    closeEditMode,
  ]);

  return (
    <StyledBody>
      {isInEditMode ? (
        <EditContainer>
          <EditTitleContainer>
            <StyledHeader>Email</StyledHeader>
            <ButtonV2
              size="middle"
              shape="round"
              type="secondary"
              dataCy="discard-email-changes"
              style={CloseCircleButtonStyle}
              onClick={closeEditMode}
            >
              <Icon icon={'closeOutlined'} size={16} color="black" />
            </ButtonV2>
          </EditTitleContainer>
          {updateProfileError && (
            <StyledCardStatus
              status="error"
              text={updateProfileError.message}
            />
          )}
          <Input
            onChange={setUpdatedEmail}
            placeholder="Email"
            value={updatedEmail}
            disabled={isFetchingProfile || isUpdatingProfile}
            required
            width="50%"
            error={
              isValidEmail(updatedEmail) ? '' : 'Please enter a valid email'
            }
            dataCy="email-input-field"
          />
          <Spacer />
          <ButtonV2
            size="middle"
            disabled={
              !updatedEmail || isUpdatingProfile || !isValidEmail(updatedEmail)
            }
            dataCy="save-button"
            style={SaveButtonWidth}
            onClick={updateEmail}
          >
            {isUpdatingProfile ? <Spin size="small" /> : 'Save'}
          </ButtonV2>
        </EditContainer>
      ) : (
        <>
          <DetailsContainer>
            <StyledHeader>Email</StyledHeader>
            {billingProfile?.Email ? (
              <StyledDescription>{billingProfile.Email}</StyledDescription>
            ) : (
              <StyledDescription>Not provided</StyledDescription>
            )}
          </DetailsContainer>

          <StyledButton
            onClick={switchToEditMode}
            color="black"
            disabled={!canManageBilling.response || !hasSavedCard}
          >
            {isUpdatingProfile ? <Spin size="small" /> : 'Update'}
          </StyledButton>
        </>
      )}
    </StyledBody>
  );
};

export default BillingEmailCard;

const StyledCardStatus = styled(CardStatus)`
  margin: 16px 0;
`;

const StyledButton = styled(Button)`
  &:disabled {
    background-color: ${Color.LIGHTER_GREY};
    color: ${Color.BORDER_GREY};
  }
`;

const StyledBody = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const EditContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledHeader = styled(Text)`
  color: ${Color.SECONDARY_TEXT};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  margin-bottom: 8px;
`;

const StyledDescription = styled(Text)`
  color: ${Color.PRIMARY_TEXT};
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
`;

const EditTitleContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: top;
`;

const CloseCircleButtonStyle = {
  width: 32,
  height: 32,
  maxWidth: 32,
  minWidth: 32,
  gap: 8,
  padding: 0,
  borderColor: Color.BORDER_GREY,
  marginLeft: 24,
};

const SaveButtonWidth = {
  width: 73,
  height: 40,
  maxWidth: 73,
  minWidth: 73,
};
